import React, { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export interface TopicFormValues {
  title: string;
  description: string;
  category: string;
}

interface TopicFormDialogProps {
  open: boolean;
  mode: "create" | "edit";
  categories: { id: number | string; name: string }[];
  initial?: Partial<TopicFormValues>;
  isLoading?: boolean;
  onClose: () => void;
  onSubmit: (values: TopicFormValues) => Promise<void> | void;
}

// Shared create/edit form for an AI Topic - same Dialog pattern used by the
// Feedback dialog and the post Edit modal elsewhere in the app.
export function TopicFormDialog({ open, mode, categories, initial, isLoading, onClose, onSubmit }: TopicFormDialogProps) {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [category, setCategory] = useState(initial?.category || "");
  const [errors, setErrors] = useState<{ title?: string; category?: string }>({});

  useEffect(() => {
    if (open) {
      setTitle(initial?.title || "");
      setDescription(initial?.description || "");
      setCategory(initial?.category || "");
      setErrors({});
    }
  }, [open, initial?.title, initial?.description, initial?.category]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: { title?: string; category?: string } = {};
    if (title.trim().length < 5) nextErrors.title = "Title must be at least 5 characters.";
    if (!category) nextErrors.category = "Category is required.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    await onSubmit({ title: title.trim(), description: description.trim(), category });
  };

  return (
    <Dialog open={open} onOpenChange={(value: boolean) => !value && !isLoading && onClose()}>
      <DialogContent className="w-full max-w-[min(100vw-1.5rem,32rem)] max-h-[90vh] overflow-y-auto rounded-2xl p-5 sm:p-6">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "New AI Topic" : "Edit AI Topic"}</DialogTitle>
          <DialogDescription>
            A topic groups several related AI Prompts together, each with its own up/down votes.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="topic-title">Title *</Label>
            <Input
              id="topic-title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (errors.title) setErrors((prev) => ({ ...prev, title: undefined }));
              }}
              placeholder="e.g. Prompt Engineering for Developers"
              aria-invalid={!!errors.title}
            />
            {errors.title && <p className="text-xs text-red-600 dark:text-red-400">{errors.title}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="topic-description">Description</Label>
            <Textarea
              id="topic-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this topic about?"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="topic-category">Category *</Label>
            <Select value={category} onValueChange={(value: string) => { setCategory(value); if (errors.category) setErrors((prev) => ({ ...prev, category: undefined })); }}>
              <SelectTrigger id="topic-category" aria-invalid={!!errors.category}>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={String(cat.id)}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.category && <p className="text-xs text-red-600 dark:text-red-400">{errors.category}</p>}
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 dark:border-slate-700 sm:flex-row sm:justify-end">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-200 dark:hover:bg-slate-100 text-white dark:text-slate-900 w-full sm:w-auto"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {mode === "create" ? "Create Topic" : "Save Changes"}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default TopicFormDialog;
